// \src\components\feature\SellerSidebar.tsx

import { useMemo } from "react";
import { useAdmin, type Seller } from "@/context/AdminContext";

const storeNames: Record<string, string> = {
  central: "Loja Central",
  filial: "MZ Outlet",
};

function getLocalDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isSellerAvailable(seller: Seller): boolean {
  if (seller.available) {
    return true;
  }

  if (!seller.inactiveUntil) {
    return false;
  }

  return seller.inactiveUntil <= getLocalDateString();
}

function getRandomSellers(all: Seller[]): Seller[] {
  const shuffled = all.filter(isSellerAvailable);

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

export default function SellerSidebar() {
  const { data } = useAdmin();
  const randomSellers = useMemo(() => getRandomSellers(data.sellers), [data.sellers]);

  return (
    <aside className="w-full">
      <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="bg-foreground-950 px-4 py-3">
          <h3 className="text-background-50 font-heading font-semibold text-sm uppercase tracking-wide">
            Nossos Vendedores
          </h3>
          <p className="text-background-400 text-xs mt-0.5">Fale diretamente com um especialista</p>
        </div>
        <div className="max-h-[520px] overflow-y-auto divide-y divide-background-100">
          {randomSellers.map((seller) => (
            <div key={seller.id} className="flex items-center gap-3 p-3">
              <img
                src={seller.photo}
                alt={seller.name}
                className="w-12 h-12 rounded-full object-cover object-top shrink-0 border-2 border-background-200"
              />
              <div className="flex-1 min-w-0">
                <p className="text-foreground-900 font-label font-semibold text-sm truncate">{seller.name}</p>
                <p className="text-foreground-600 text-xs truncate">{seller.role}</p>
                <p className="text-foreground-400 text-xs truncate mt-0.5">{storeNames[seller.store] || seller.store}</p>
                <div className="flex items-center gap-1 mt-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" />
                  <span className="text-xs text-green-600">Disponível</span>
                </div>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <a
                  href={`tel:+${seller.phone}`}
                  className="w-8 h-8 flex items-center justify-center bg-foreground-900 hover:bg-primary-500 text-background-50 rounded-md transition-colors cursor-pointer"
                  title={`Ligar para ${seller.name}`}
                >
                  <i className="ri-phone-line text-sm" />
                </a>
                <a
                  href={`https://wa.me/${seller.whatsapp}`}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="w-8 h-8 flex items-center justify-center bg-green-600 hover:bg-green-700 text-background-50 rounded-md transition-colors cursor-pointer"
                  title={`WhatsApp de ${seller.name}`}
                >
                  <i className="ri-whatsapp-line text-sm" />
                </a>
              </div>
            </div>
          ))}
        </div>
        <div className="p-3 bg-background-100">
          <p className="text-xs text-foreground-500 text-center">
            Atendimento: Seg–Sex 07h–18h | Sáb 07h–13h
          </p>
        </div>
      </div>
    </aside>
  );
}